import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, computed, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { sanitizeText } from 'src/app/common/input-utils';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { resolveSearchErrorMessage } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { AsociacionCatalogoConfig } from './asociacion-catalogo.config';

/**
 * Diálogo genérico de selección de ítems del catálogo — el "agregar" de
 * HU1259149 (Fase 3). Reutilizado por los 5 catálogos vía
 * {@link AsociacionCatalogoConfig}, mismo patrón de
 * `app-busqueda-aseguradora-dialog` (filtros + `p-table` paginado +
 * `BusquedaPaginadorComponent`) — selección múltiple por checkbox.
 */
@Component({
  selector: 'app-seleccionar-item-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, TableModule, InputTextModule, DatePicker, InputNumberModule, ButtonModule, BusquedaPaginadorComponent],
  template: `
    <p-dialog
      [header]="'Agregar ' + config.tituloLista"
      [(visible)]="visibleInterno"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '48rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="cerrar()"
    >
      @if (config.requiereCliente && !nmconsecutivoCliente) {
        <div class="p-3 mb-3 rounded bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm">
          Seleccione primero un cliente para buscar en este catálogo.
        </div>
      }

      @if (config.filtrosCatalogo.length > 0) {
        <div class="border dark:border-2 rounded p-3 mb-3 bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            @for (filtro of config.filtrosCatalogo; track filtro.campo) {
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">{{ filtro.label }}</label>
                <input
                  pInputText
                  class="w-full"
                  maxlength="100"
                  [placeholder]="filtro.placeholder ?? ''"
                  [ngModel]="filtros()[filtro.campo]"
                  (ngModelChange)="onFiltroChange(filtro.campo, $event)"
                />
              </div>
            }
          </div>
          <div class="flex justify-end mt-3">
            <p-button label="Buscar" icon="pi pi-search" styleClass="min-h-[44px]" (onClick)="buscar()"></p-button>
          </div>
        </div>
      }

      @if (mensajeError()) {
        <div class="p-3 mb-3 rounded bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm">{{ mensajeError() }}</div>
      }

      <p-table
        [value]="items()"
        [loading]="cargando()"
        [(selection)]="seleccionados"
        dataKey="__id"
        [scrollable]="true"
        [responsiveLayout]="'scroll'"
        [rowHover]="true"
        scrollHeight="280px"
        [tableStyle]="{ 'min-width': '100%' }"
        styleClass="p-datatable-sm p-datatable-gridlines p-datatable-striped"
      >
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 3rem"></th>
            @for (columna of config.columnasCatalogo; track columna.campo) {
              <th>{{ columna.header }}</th>
            }
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-fila>
          <tr>
            <td>
              <p-tableCheckbox [value]="fila"></p-tableCheckbox>
            </td>
            @for (columna of config.columnasCatalogo; track columna.campo) {
              <td [attr.title]="fila[columna.campo]">{{ fila[columna.campo] }}</td>
            }
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td [attr.colspan]="config.columnasCatalogo.length + 1" class="text-center text-gray-500 py-4">
            {{ cargando() ? 'Buscando...' : 'Sin resultados.' }}
          </td></tr>
        </ng-template>
      </p-table>

      <app-busqueda-paginador
        [currentPage]="currentPage()"
        [totalPages]="totalPages()"
        [hasPrevious]="hasPrevious()"
        [hasNext]="hasNext()"
        [pageSizeOptions]="pageSizeOptions"
        [selectedPageSize]="pageSize()"
        (pageSizeChange)="onPageSizeChange($event)"
        (first)="irPrimeraPagina()"
        (previous)="paginaAnterior()"
        (next)="paginaSiguiente()"
        (last)="irUltimaPagina()"
      ></app-busqueda-paginador>

      @if ((config.camposAltaLote ?? []).length > 0) {
        <div class="border dark:border-2 rounded p-3 mt-3 bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            @for (campo of config.camposAltaLote ?? []; track campo.campo) {
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                  {{ campo.label }}@if (campo.requerido) {<span class="text-red-600"> *</span>}
                </label>
                @if (campo.tipo === 'fecha') {
                  <p-datepicker
                    [ngModel]="valoresAlta()[campo.campo]"
                    (ngModelChange)="onValorAltaChange(campo.campo, $event)"
                    dateFormat="yy/mm/dd"
                    [showIcon]="true"
                    appendTo="body"
                    styleClass="w-full"
                  ></p-datepicker>
                } @else if (campo.tipo === 'numero') {
                  <p-inputNumber
                    [ngModel]="valoresAlta()[campo.campo]"
                    (ngModelChange)="onValorAltaChange(campo.campo, $event)"
                    [useGrouping]="false"
                    [min]="campo.min ?? null"
                    [max]="campo.max ?? null"
                    styleClass="w-full"
                  ></p-inputNumber>
                } @else {
                  <input
                    pInputText
                    class="w-full"
                    maxlength="100"
                    [ngModel]="valoresAlta()[campo.campo]"
                    (ngModelChange)="onValorAltaChange(campo.campo, sanitizeText($event))"
                  />
                }
              </div>
            }
          </div>
        </div>
      }

      @if (mensajeValidacion()) {
        <div class="p-3 mt-3 rounded bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm">
          {{ mensajeValidacion() }}
        </div>
      }

      <div class="flex flex-wrap justify-end gap-2 mt-4">
        <p-button label="Cancelar" icon="pi pi-times" [outlined]="true" styleClass="min-h-[44px]" (onClick)="cerrar()"></p-button>
        <p-button
          [label]="'Agregar (' + seleccionados.length + ')'"
          icon="pi pi-check"
          styleClass="min-h-[44px]"
          [disabled]="seleccionados.length === 0"
          (onClick)="confirmar()"
        ></p-button>
      </div>
    </p-dialog>
  `
})
export class SeleccionarItemDialogComponent<TItem = unknown> implements OnChanges {

  @Input({ required: true }) visible = false;
  @Input({ required: true }) config!: AsociacionCatalogoConfig<unknown, TItem>;
  @Input() nmconsecutivoCliente: number | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() seleccionConfirmada = new EventEmitter<{ items: TItem[]; valoresAlta: Record<string, unknown> }>();

  protected readonly sanitizeText = sanitizeText;
  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  protected readonly items = signal<TItem[]>([]);
  protected readonly cargando = signal(false);
  protected readonly mensajeError = signal<string | null>(null);
  protected readonly hasNext = signal(false);
  protected readonly hasPrevious = signal(false);
  protected readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  protected readonly filtros = signal<Record<string, string>>({});

  /**
   * Paginación real (frontend-girop §17/§8 — auditoría de tablas). Ver mismo
   * patrón en `AsociacionCatalogoComponent`. Todos los catálogos de
   * selección exponen `totalElements` (`acceso-datos-oracle` §7 —
   * `countByFilters` agregado a Especialidad/DetalleZona/
   * ClienteUnidadNegocio, que quedó sin ese quinto método al construirse en
   * Fase 2.5); `Math.max(currentPage, ...)` se deja como resguardo
   * defensivo por si algún consumidor futuro no lo calcula.
   */
  protected readonly currentPage = signal(1);
  protected readonly totalElements = signal(0);
  protected readonly totalPages = computed(() =>
    Math.max(this.currentPage(), Math.ceil(this.totalElements() / this.pageSize()) || 1));
  /** Valores de {@code camposAltaLote} (p. ej. "Fecha Inicio" de Servicios, C-1). */
  protected readonly valoresAlta = signal<Record<string, unknown>>({});
  protected readonly mensajeValidacion = signal<string | null>(null);

  protected seleccionados: TItem[] = [];

  /** Cursores reales de la última página cargada (keyset) — ver `buscar()`. */
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.seleccionados = [];
      this.currentPage.set(1);
      this.nextCursorActual = null;
      this.previousCursorActual = null;
      this.valoresAlta.set({});
      this.mensajeValidacion.set(null);
      this.buscar();
    }
  }

  onValorAltaChange(campo: string, valor: unknown): void {
    this.valoresAlta.set({ ...this.valoresAlta(), [campo]: valor });
  }

  get visibleInterno(): boolean {
    return this.visible;
  }

  set visibleInterno(value: boolean) {
    this.visibleChange.emit(value);
  }

  onFiltroChange(campo: string, valor: string): void {
    this.filtros.set({ ...this.filtros(), [campo]: sanitizeText(valor) });
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
    this.buscar();
  }

  /**
   * @param direction NEXT (por defecto) o PREVIOUS — PREVIOUS sin cursor
   * equivale a "ir a la última página" en el backend keyset (igual que
   * `AsociacionCatalogoComponent.cargar()` y `acceso-datos-oracle` §7).
   * @param cursorOverride cursor explícito; si se omite, se usa el cursor de
   * la última página cargada para `direction`.
   */
  async buscar(direction: 'NEXT' | 'PREVIOUS' = 'NEXT', cursorOverride?: string | null): Promise<void> {
    const cfg = this.config;
    // La selección no sobrevive un cambio de página/filtro/búsqueda: el
    // legado selecciona sobre una grilla externamente paginada
    // (`setExternalPagination(true)`), donde las filas (y sus checkboxes)
    // se reconstruyen en cada página — no hay selección acumulada entre
    // páginas. Además de fidelidad, esto acota de forma natural (sin
    // inventar un límite) el tamaño del lote que se puede enviar de una
    // sola vez al mismo tope que ya rige toda la HU (`MAX_PAGE_SIZE`,
    // ver `@Size(max)` en los 5 controllers).
    this.seleccionados = [];
    if (cfg.requiereCliente && !this.nmconsecutivoCliente) {
      this.items.set([]);
      return;
    }
    this.cargando.set(true);
    this.mensajeError.set(null);
    try {
      const cursor = cursorOverride !== undefined
        ? cursorOverride
        : (direction === 'NEXT' ? this.nextCursorActual : this.previousCursorActual);
      const page = await cfg.buscarCatalogo(this.filtros(), this.nmconsecutivoCliente, this.pageSize(), cursor ?? undefined, direction);
      this.items.set(page.content.map((item) => ({ ...item, __id: cfg.idItemCatalogo(item) } as TItem)));
      this.hasNext.set(page.hasNext);
      this.hasPrevious.set(page.hasPrevious);
      this.totalElements.set(page.totalElements ?? 0);
      this.nextCursorActual = page.nextCursor;
      this.previousCursorActual = page.previousCursor;

      if (direction === 'PREVIOUS' && cursor == null) {
        const size = this.pageSize();
        const total = this.totalElements();
        this.currentPage.set(size > 0 && total > 0 ? Math.ceil(total / size) : 1);
      }
    } catch (error) {
      this.mensajeError.set(resolveSearchErrorMessage(error, 'No fue posible consultar el catálogo.'));
      this.items.set([]);
    } finally {
      this.cargando.set(false);
    }
  }

  irPrimeraPagina(): void {
    this.currentPage.set(1);
    this.buscar('NEXT', null);
  }

  irUltimaPagina(): void {
    this.buscar('PREVIOUS', null);
  }

  paginaSiguiente(): void {
    if (!this.hasNext()) {
      return;
    }
    this.currentPage.update(p => p + 1);
    this.buscar('NEXT');
  }

  paginaAnterior(): void {
    if (!this.hasPrevious()) {
      return;
    }
    this.currentPage.update(p => Math.max(1, p - 1));
    this.buscar('PREVIOUS');
  }

  confirmar(): void {
    // `!valor` trataría 0 como "vacío" (falso positivo en campos numéricos
    // como Calificación, C-5): solo null/undefined/'' cuentan como faltante.
    const faltante = (this.config.camposAltaLote ?? [])
      .find((campo) => campo.requerido && this.valorAltaVacio(this.valoresAlta()[campo.campo]));

    if (faltante) {
      // El legado marca estos campos como obligatorios en el diálogo
      // (setObligatorio/setRequired) y no deja grabar sin ellos — C-1.
      this.mensajeValidacion.set(`${faltante.label} es obligatorio.`);
      return;
    }

    this.mensajeValidacion.set(null);
    this.seleccionConfirmada.emit({ items: this.seleccionados, valoresAlta: this.valoresAlta() });
    this.cerrar();
  }

  private valorAltaVacio(valor: unknown): boolean {
    return valor === null || valor === undefined || valor === '';
  }

  cerrar(): void {
    this.visibleChange.emit(false);
  }
}
